import type { StatusLevel } from '@westhetics/ui'
import type { ClinicUser, MemberRole } from './api'

/** Os perfis de quem entra numa clínica, por extenso (docs/areas/autenticacao.md, "Perfis"). */
export const ROLE_LABELS: Record<MemberRole, string> = {
  manager: 'Gestora',
  receptionist: 'Recepção',
  practitioner: 'Prestador',
}

/** O que cada perfil faz, na linha de apoio da escolha. */
export const ROLE_DESCRIPTIONS: Record<MemberRole, string> = {
  manager: 'Dona ou administradora da clínica: cuida de usuários e cadastros e vê o faturamento.',
  receptionist: 'Cadastra paciente, gera e envia documentos, confere o dia.',
  practitioner: 'Profissional que aplica; vê a anamnese só dos seus pacientes.',
}

export const ROLES: MemberRole[] = ['manager', 'receptionist', 'practitioner']

/**
 * O status do vínculo por extenso (docs/areas/autenticacao.md, "Estados e transições"). Quem ainda
 * não definiu a senha espera a própria pessoa, não quem administra: nível `waiting`.
 */
export function userStatus(user: Pick<ClinicUser, 'status'>): {
  level: StatusLevel
  label: string
} {
  if (user.status === 'blocked') return { level: 'attention', label: 'Bloqueado' }
  if (user.status === 'invited') return { level: 'waiting', label: 'Aguardando primeiro acesso' }
  if (user.status === 'removed') return { level: 'waiting', label: 'Removido' }
  return { level: 'done', label: 'Ativo' }
}

/** O convite por e-mail ainda não foi aceito: a saída é reenviar, não resetar (RN-ADM-14). */
export function hasPendingInvite(user: Pick<ClinicUser, 'inviteExpiresAt'>) {
  return user.inviteExpiresAt !== null
}

/**
 * Ainda não definiu a senha e não tem convite emitido: foi criada com senha temporária, ou veio da
 * carga do sistema antigo sem senha nenhuma. O objeto não diz qual das duas; no segundo caso a API
 * recusa o reset e manda reenviar o convite. Por isso a tela oferece também o convite por e-mail.
 */
export function canSendInvite(user: Pick<ClinicUser, 'inviteExpiresAt' | 'status'>) {
  return user.status === 'invited' && user.inviteExpiresAt === null
}

/**
 * A âncora do cartão "Remover da clínica" na edição. "remover" na lista leva até ele: é lá que a
 * justificativa é escrita, antes do modal (P-ADM-30).
 */
export const REMOVE_ANCHOR = 'remove'

export const MSG_JUSTIFICATION = 'Escreva o motivo em 10 a 500 caracteres.'

/** A justificativa que vai para a trilha: 10 a 500 caracteres (RT-22). */
export function isJustification(text: string) {
  const length = text.trim().length
  return length >= 10 && length <= 500
}
